import { ModalLayout } from '@/components/HeadlessUI'
import { MODAL_PANEL_CLASS_MD, MODAL_PLACEMENT_CENTER } from '@/constants'
import { useEffect, useState } from 'react'

const TERMS_PDF_URL = '/assets/Terms/TermsandConditions.pdf#zoom=90&navpanes=0'

type TermsAndConditionsModalProps = {
	isOpen: boolean
	onClose: () => void
	onContinue: () => void
	title?: string
	description?: string
	hideAcceptanceControls?: boolean
	pdfUrl?: string
	acceptanceLabel?: string
	continueLabel?: string
}

const TermsAndConditionsModal = ({
	isOpen,
	onClose,
	onContinue,
	title = 'Terms and Conditions',
	description = 'Review and accept before adding your first payment card.',
	hideAcceptanceControls = false,
	pdfUrl = TERMS_PDF_URL,
	acceptanceLabel = 'I have read and agree to the Terms and Conditions.',
	continueLabel = 'Continue',
}: TermsAndConditionsModalProps) => {
	const [accepted, setAccepted] = useState(false)

	useEffect(() => {
		if (isOpen) {
			setAccepted(false)
		}
	}, [isOpen])

	const handleContinue = () => {
		if (!accepted) return
		onContinue()
	}

	return (
		<ModalLayout
			showModal={isOpen}
			placement={MODAL_PLACEMENT_CENTER}
			toggleModal={onClose}
			panelClassName={MODAL_PANEL_CLASS_MD}
			transitionVariant="default"
			isStatic
		>
			<div className="overflow-hidden rounded-app">
				<div className="relative border-b border-border/60 bg-card px-6 py-5">
					<button
						type="button"
						className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
						onClick={onClose}
						aria-label="Close"
					>
						<i className="ri-close-line text-xl" />
					</button>
					<div className="flex items-start gap-3 pr-8">
						<div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
							<i className="ri-file-shield-2-line text-2xl" aria-hidden />
						</div>
						<div>
							<h3 className="text-lg font-semibold text-foreground">{title}</h3>
							<p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
						</div>
					</div>
				</div>
				<div className="space-y-4 px-6 py-5">
					<div className="h-[60vh] overflow-hidden rounded-lg border border-border/60 bg-secondary/20">
						<iframe
							src={pdfUrl}
							title={title}
							className="h-full w-full"
						/>
					</div>
					{!hideAcceptanceControls && (
						<label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border/60 bg-secondary/10 px-4 py-3 text-sm text-muted-foreground">
							<span className="relative mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center">
								<input
									type="checkbox"
									className="peer absolute inset-0 z-10 h-5 w-5 cursor-pointer opacity-0"
									checked={accepted}
									onChange={(e) => setAccepted(e.target.checked)}
								/>
								<span className="pointer-events-none absolute inset-0 rounded-[0.4rem] border-2 border-[#a8564a] bg-[#2a1010] transition-all peer-checked:border-transparent peer-checked:bg-gradient-to-br peer-checked:from-[#ff8f5e] peer-checked:to-[#e11d2a]" />
								<i className="ri-check-line pointer-events-none relative hidden text-[0.8rem] text-white peer-checked:block" aria-hidden />
							</span>
							<span>{acceptanceLabel}</span>
						</label>
					)}
				</div>
				{!hideAcceptanceControls && (
					<div className="flex justify-end gap-2 border-t border-border/50 px-6 py-4">
						<button type="button" className="btn btn-secondary" onClick={onClose}>
							Cancel
						</button>
						<button type="button" className="btn bg-primary text-white" onClick={handleContinue} disabled={!accepted}>
							{continueLabel}
						</button>
					</div>
				)}
			</div>
		</ModalLayout>
	)
}

export default TermsAndConditionsModal
